import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import {
  setPageTitle,
  toggleNavigation,
  toggleBreadcrum,
} from "../../../store/Page";
import {
  getDashboardOverviewVideoUrl,
  getLoomEmbedUrl,
  fetchLoomThumbnailUrl,
} from "../../../config/pageTutorials";
import { selectUser } from "../../../store/userSelectors";
import {
  NEXT_LAUNCHPAD_TARGETS,
  isNextLaunchpadEnabledForUser,
  isNextLaunchpadRefinementsEnabledForUser,
  openNextLaunchpad,
} from "../../../util/nextLaunchpad";
import "./DashboardOverview.css";

const DEFAULT_THEME_PRIMARY = "#087ab6";

const getDashboardOverviewTheme = (user) => {
  const theme = user?.customizedDashboard?.[0];
  const headerColor = theme?.header_color?.trim() || "";
  const secondaryHeaderColor = theme?.secondary_header_color?.trim() || "";

  return {
    primaryColor:
      headerColor || secondaryHeaderColor || DEFAULT_THEME_PRIMARY,
  };
};

const overviewLinks = [
  {
    id: "reporting",
    title: "View Calls & Reporting",
    panelDescription:
      "See how to access call activity, performance insights, and detailed reports.",
    description:
      "Access your call activity, performance insights, and detailed reports.",
    icon: "fa fa-bar-chart",
    videoKey: "reporting",
    path: "/chat-dashboard",
  },
  {
    id: "billing",
    title: "Update Plans & Billing",
    panelDescription:
      "See how to manage your subscription, billing history, and payment methods.",
    description:
      "Manage your subscription, view billing history, and update payment methods.",
    icon: "fa fa-credit-card",
    videoKey: "billing",
    path: "/settings?tab=billing",
  },
  {
    id: "manageUsers",
    title: "Add Users",
    panelDescription:
      "See how to invite team members and manage user roles and permissions.",
    description: "Invite team members and manage user roles and permissions.",
    icon: "fa fa-users",
    videoKey: "manageUsers",
    path: "/settings?tab=manageuser",
  },
  {
    id: "profile",
    title: "Make Changes",
    panelDescription:
      "See how to customize settings, preferences, and other configurations.",
    description:
      "Customize settings, preferences, and other configurations to fit your needs.",
    icon: "fa fa-cog",
    videoKey: "profile",
    path: "/settings?tab=profile",
    launchpadTarget: NEXT_LAUNCHPAD_TARGETS.PARTNER_REFINEMENTS,
  },
  {
    id: "support",
    title: "Get Support",
    panelDescription:
      "See how to find help articles, contact support, and get the answers you need.",
    description:
      "Find help articles, contact support, and get the answers you need.",
    icon: "fa fa-question-circle",
    videoKey: "supportTickets",
    path: "/settings?tab=support",
  },
];

const DashboardOverview = ({ history, user, setPageTitle, toggleNavigation, toggleBreadcrum }) => {
  const [activeLinkId, setActiveLinkId] = useState(overviewLinks[0].id);
  const [nextLaunchpadLoadingTarget, setNextLaunchpadLoadingTarget] = useState("");
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [videoLoadError, setVideoLoadError] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [isThumbnailLoading, setIsThumbnailLoading] = useState(false);

  const activeLink = useMemo(
    () => overviewLinks.find((link) => link.id === activeLinkId) || overviewLinks[0],
    [activeLinkId]
  );

  const plgAccountType = user?.plg_account_type;

  const videoShareUrl = useMemo(
    () => getDashboardOverviewVideoUrl(activeLink.videoKey, plgAccountType),
    [activeLink.videoKey, plgAccountType]
  );

  const videoEmbedSrc = useMemo(() => {
    if (!isVideoLoaded) {
      return "";
    }

    return getLoomEmbedUrl(videoShareUrl, { autoplay: true });
  }, [videoShareUrl, isVideoLoaded]);

  const themeStyle = useMemo(() => {
    const { primaryColor } = getDashboardOverviewTheme(user);
    return { "--do-theme-primary": primaryColor };
  }, [user]);

  useEffect(() => {
    setPageTitle({
      title: "Dashboard Overview",
      subHeaderTitle: "Dashboard Overview",
    });
    toggleNavigation({ toggle: false });
    toggleBreadcrum({ toggle: false });
  }, [setPageTitle, toggleNavigation, toggleBreadcrum]);

  useEffect(() => {
    setIsVideoLoaded(false);
    setIsVideoPlaying(false);
    setIsVideoLoading(false);
    setVideoLoadError(false);
  }, [activeLinkId, videoShareUrl]);

  useEffect(() => {
    let isCancelled = false;

    if (!videoShareUrl) {
      setThumbnailUrl("");
      setIsThumbnailLoading(false);
      return undefined;
    }

    setIsThumbnailLoading(true);
    setThumbnailUrl("");

    fetchLoomThumbnailUrl(videoShareUrl).then((url) => {
      if (isCancelled) {
        return;
      }

      setThumbnailUrl(url);
      setIsThumbnailLoading(false);
    });

    return () => {
      isCancelled = true;
    };
  }, [videoShareUrl]);

  const handleVideoLoad = () => {
    setIsVideoLoading(false);
    setVideoLoadError(false);
  };

  const handleVideoError = () => {
    setIsVideoLoading(false);
    setVideoLoadError(true);
    setIsVideoPlaying(false);
  };

  const handleTogglePlayback = () => {
    if (!videoShareUrl) {
      return;
    }

    if (!isVideoLoaded || !isVideoPlaying) {
      setIsVideoLoaded(true);
      setIsVideoPlaying(true);
      setIsVideoLoading(true);
      setVideoLoadError(false);
      return;
    }

    setIsVideoLoaded(false);
    setIsVideoPlaying(false);
    setIsVideoLoading(false);
    setVideoLoadError(false);
  };

  const showThumbnail = !isVideoLoaded || isVideoLoading;
  const showThumbnailLoader = showThumbnail && isThumbnailLoading;

  const handleSelectLink = (linkId) => {
    setActiveLinkId(linkId);
  };

  const canOpenLaunchpadTarget = (target) => {
    if (target === NEXT_LAUNCHPAD_TARGETS.PARTNER_REFINEMENTS) {
      return isNextLaunchpadRefinementsEnabledForUser({ user });
    }

    return isNextLaunchpadEnabledForUser({ user });
  };

  const handleVisitPage = async (event, link) => {
    event.stopPropagation();
    event.preventDefault();
    if (link.launchpadTarget && canOpenLaunchpadTarget(link.launchpadTarget)) {
      setNextLaunchpadLoadingTarget(link.launchpadTarget);

      try {
        await openNextLaunchpad({ user, target: link.launchpadTarget });
      } catch (err) {
        alert("AI Launchpad is not available right now. Please try again.");
      } finally {
        setNextLaunchpadLoadingTarget("");
      }
      return;
    }
    history.push(link.path);
  };

  return (
    <div
      className="kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor dashboard-overview-page"
      id="dashboardOverviewPage"
      style={themeStyle}
    >
      <div className="kt-content kt-grid__item kt-grid__item--fluid">
        <div className="yr-body-p dashboard-overview-body">
          <div className="dashboard-overview-layout">
            <span className="dashboard-overview-badge">🎉 You&apos;re all set!</span>
            <div className="dashboard-overview-content">
              <h1 className="dashboard-overview-title">Welcome to Zyratalk</h1>
              <p className="dashboard-overview-subtitle">
                Everything you need to connect, communicate, and grow — all in one
                place.
              </p>
              <p className="dashboard-overview-video-intro">
                We have you covered! Take a look at the following videos to get a
                full dashboard overview.
              </p>

              <ul className="dashboard-overview-links">
                {overviewLinks.map((link) => {
                  const isActive = link.id === activeLinkId;
                  const isOpeningLaunchpad =
                    link.launchpadTarget &&
                    nextLaunchpadLoadingTarget === link.launchpadTarget;

                  return (
                    <li key={link.id}>
                      <div
                        className={`dashboard-overview-link-card${
                          isActive ? " dashboard-overview-link-card--active" : ""
                        }`}
                      >
                        <button
                          type="button"
                          className="dashboard-overview-link-card-main"
                          aria-pressed={isActive}
                          onClick={() => handleSelectLink(link.id)}
                        >
                          <span className="dashboard-overview-link-icon">
                            <i className={link.icon} aria-hidden="true" />
                          </span>
                          <span className="dashboard-overview-link-copy">
                            <span className="dashboard-overview-link-title">
                              {link.title}
                            </span>
                            <span className="dashboard-overview-link-description">
                              {link.description}
                            </span>
                            {isActive && (
                              <span className="dashboard-overview-currently-playing">
                                <i className="fa fa-play-circle" aria-hidden="true" />
                                Currently playing
                              </span>
                            )}
                          </span>
                        </button>
                        <div className="dashboard-overview-visit-page-action">
                          <button
                            type="button"
                            className="dashboard-overview-visit-page-btn"
                            onClick={(event) => handleVisitPage(event, link)}
                            disabled={isOpeningLaunchpad}
                          >
                            Visit this page
                          </button>
                          <i
                            className="fa fa-chevron-right dashboard-overview-visit-page-icon"
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="dashboard-overview-video-panel">
              <div className="dashboard-overview-video-card">
                <div className="dashboard-overview-video-header">
                  <h2 className="dashboard-overview-video-title">
                    {activeLink.title} Overview
                  </h2>
                  <p className="dashboard-overview-video-description">
                    {activeLink.panelDescription}
                  </p>
                </div>

                <div className="dashboard-overview-video-player">
                  <div className="dashboard-overview-video-embed">
                    {showThumbnail && thumbnailUrl && (
                      <img
                        key={thumbnailUrl}
                        src={thumbnailUrl}
                        alt=""
                        className="dashboard-overview-video-thumbnail"
                      />
                    )}
                    {showThumbnailLoader && (
                      <div
                        className="dashboard-overview-video-loading dashboard-overview-video-loading--thumbnail"
                        aria-live="polite"
                        aria-busy="true"
                      >
                        <span
                          className="dashboard-overview-video-loading__spinner"
                          aria-hidden="true"
                        />
                      </div>
                    )}
                    {showThumbnail && !thumbnailUrl && !isThumbnailLoading && (
                      <div
                        className="dashboard-overview-video-thumbnail dashboard-overview-video-thumbnail--empty"
                        aria-hidden="true"
                      />
                    )}
                    {isVideoLoading && (
                      <div
                        className="dashboard-overview-video-loading"
                        aria-live="polite"
                        aria-busy="true"
                      >
                        <span
                          className="dashboard-overview-video-loading__spinner"
                          aria-hidden="true"
                        />
                        <span className="dashboard-overview-video-loading__text">
                          Loading video...
                        </span>
                      </div>
                    )}
                    {videoLoadError && (
                      <div className="dashboard-overview-video-loading" aria-live="polite">
                        <span className="dashboard-overview-video-loading__text">
                          Unable to load video. Please try again.
                        </span>
                      </div>
                    )}
                    {videoEmbedSrc && (
                      <iframe
                        key={videoEmbedSrc}
                        title={`${activeLink.title} tutorial`}
                        src={videoEmbedSrc}
                        className={`dashboard-overview-video-iframe${
                          isVideoLoading ? " dashboard-overview-video-iframe--loading" : ""
                        }`}
                        onLoad={handleVideoLoad}
                        onError={handleVideoError}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                    )}
                  </div>

                  <button
                    type="button"
                    className="dashboard-overview-play-video-btn"
                    onClick={handleTogglePlayback}
                    disabled={!videoShareUrl}
                    aria-label={
                      isVideoPlaying
                        ? `Pause ${activeLink.title} tutorial`
                        : `Play ${activeLink.title} tutorial`
                    }
                  >
                    <span className="dashboard-overview-video-play">
                      <i
                        className={`fa ${isVideoPlaying ? "fa-pause" : "fa-play"}`}
                        aria-hidden="true"
                      />
                    </span>
                    <span className="dashboard-overview-video-label">
                      {isVideoPlaying ? "Pause Video" : "Play Video"}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(
  (state) => ({
    user: selectUser(state),
  }),
  {
    setPageTitle,
    toggleNavigation,
    toggleBreadcrum,
  }
)(withRouter(DashboardOverview));
